<script setup>
/**
 * A GAVETA LATERAL do módulo Minutas (29/09/2026): a casca da folha que
 * desliza da direita — véu, topo escuro com selo e fechar, corpo que rola,
 * rodapé de ações. Acessível pela diretiva do produto (`v-modal-acessivel`:
 * papel, nome, foco que entra, Tab preso, Escape, foco devolvido).
 *
 * Quem usa preenche: `#passos` (entre o topo e o corpo), o padrão (o corpo)
 * e `#rodape`. Desenho em `_advup-gaveta.scss`.
 */
import { onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

defineProps({
  aberta: { type: Boolean, default: false },
  titulo: { type: String, required: true },
  sub: { type: String, default: '' },
  icone: { type: String, default: 'i-ph-file-text-fill' },
  /** Tom do topo, o mesmo do modal: '', 'teal', 'ambar', 'rubi', 'iris'. */
  tom: { type: String, default: '' },
  larga: { type: Boolean, default: false },
  ocupada: { type: Boolean, default: false },
  /**
   * O foco entra no título, não no primeiro campo: gaveta de ESCOLHER
   * (cartões), não de escrever. Sem isto a de assinatura focava o campo de
   * arquivo — anel azul à toa e, no celular, a gaveta abria rolada.
   */
  focoNoTitulo: { type: Boolean, default: false },
  /**
   * AO LADO, e não por cima (29/10/2026 — Victor, montando assinaturas: a
   * pessoa precisa olhar os dados do cliente na conversa enquanto preenche, e
   * o véu borrava tudo; clicar fora fechava a folha e perdia o que estava
   * escrito). Sem véu, a página atrás fica nítida e usável e abre espaço à
   * direita; clicar fora NÃO fecha; Escape e Tab só valem dentro da folha.
   * No celular a folha é a tela inteira, como sempre.
   */
  aoLado: { type: Boolean, default: false },
});

const emit = defineEmits(['fechar']);
const { t } = useI18n();
const fechar = () => emit('fechar');

// A página abre espaço enquanto a folha ao lado está aberta (`_advup-gaveta.scss`).
const CLASSE_DO_CORPO = 'advup-com-gaveta-ao-lado';
const abriuAoLado = () => document.body.classList.add(CLASSE_DO_CORPO);
const fechouAoLado = () => document.body.classList.remove(CLASSE_DO_CORPO);
onBeforeUnmount(fechouAoLado);
</script>

<template>
  <Teleport to="body">
    <Transition
      name="advup-gaveta"
      @before-enter="aoLado && abriuAoLado()"
      @after-leave="fechouAoLado"
    >
      <div
        v-if="aberta"
        class="advup-gaveta-veu"
        :class="{ 'advup-gaveta-veu--ao-lado': aoLado }"
        @mousedown.self="!aoLado && fechar()"
      >
        <!-- `advup-modal--claro` (04/10/2026): o topo claro da Agenda (véu do
             tom, selo sólido, marca d'água) — era o degradê escuro, §9. -->
        <aside
          v-modal-acessivel="fechar"
          class="advup-gaveta advup-modal--claro"
          :class="{ 'advup-gaveta--larga': larga }"
          :aria-busy="ocupada"
          :data-nao-modal="aoLado || null"
        >
          <header
            class="advup-modal__topo advup-gaveta__topo"
            :class="tom ? `advup-modal__topo--${tom}` : ''"
          >
            <i class="advup-modal__marca" :class="icone" aria-hidden="true" />
            <span class="advup-modal__selo" aria-hidden="true">
              <i :class="icone" />
            </span>
            <div class="advup-gaveta__id">
              <h2
                class="advup-modal__titulo"
                :data-foco-inicial="focoNoTitulo || null"
              >
                {{ titulo }}
              </h2>
              <p v-if="sub" class="advup-modal__sub">{{ sub }}</p>
            </div>
            <slot name="topo-acoes" />
            <button
              type="button"
              class="advup-gaveta__fechar"
              :aria-label="t('ASSINATURAS.GAVETA.FECHAR')"
              @click="fechar"
            >
              <i class="i-hugeicons-cancel-01" aria-hidden="true" />
            </button>
          </header>
          <slot name="passos" />
          <div class="advup-gaveta__corpo">
            <slot />
          </div>
          <footer
            v-if="$slots.rodape"
            class="advup-modal__rodape advup-modal__rodape--acoes advup-gaveta__rodape"
          >
            <slot name="rodape" />
          </footer>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>
